import { useHistory } from "react-router-dom";
import { Stack } from "@mui/material";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";

import { AmountInCustomerCurrency } from "../AmountInCustomerCurrency";
import { CardPager } from "../CardPager";
import EmptyList from "../EmptyList";
import HomePageCard from "../HomePageCard";
import { BudgetRow } from "../Rows/BudgetRow";
import { type ParsedBudget } from "../types";
import { ITEMS_PER_PAGE, usePagination } from "./hooks";

type ActiveBudgetsListProps = {
  budgets: ParsedBudget[];
  budgetsAtRisk: number;
};

const ActiveBudgetsList = ({ budgets, budgetsAtRisk }: ActiveBudgetsListProps) => {
  const history = useHistory();
  const { currentPageItems, currentPage, startItem, endItem, totalItems, handlePageChange } = usePagination(budgets);

  if (budgets.length === 0) {
    return (
      <EmptyList
        title="No budgets to action yet"
        subheader="Reduce risk by taking action and acknowledging anomalies"
        description="When budgets are created you will be able to track them here and take action to reduce waste and risk "
        buttonText="Create a budget"
        buttonDataCy="implemented-budgets-btn"
        onButtonClick={() => {
          history.push("./monitor/budgets/create");
        }}
      />
    );
  }

  return (
    <HomePageCard
      title="Active Budgets"
      subheader={
        <Typography variant="body2" color="text.secondary" noWrap>
          {budgetsAtRisk > 0 ? (
            <Typography variant="body2" color="text.secondary" noWrap>
              Budgets are nearing or exceeding their total amounts with a{" "}
              <b>
                <AmountInCustomerCurrency amount={budgetsAtRisk} dontConvert />
              </b>{" "}
              overrun forecasted
            </Typography>
          ) : (
            "Active budgets by nearest to total budget amount"
          )}
        </Typography>
      }
    >
      <Stack sx={{ minHeight: 479, height: "100%", justifyContent: "space-between" }}>
        <List dense sx={{ pb: 0, pt: 0 }}>
          {currentPageItems.map((budget, i) => (
            <BudgetRow
              key={`${budget.name}-${i}`}
              budget={budget}
              hideDivider={i === currentPageItems.length - 1 && currentPageItems.length === ITEMS_PER_PAGE}
            />
          ))}
        </List>
        <CardPager
          totalItems={totalItems}
          currentPage={currentPage}
          startItem={startItem}
          endItem={endItem}
          onPageChange={handlePageChange}
        />
      </Stack>
    </HomePageCard>
  );
};

export default ActiveBudgetsList;
